import { OptionRelatedInstituteSettings, ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { VerityInstituteSettings } from "../../types/common";
import { ProctoringDataType } from "../hooks/state/useSettingsState";
import { useId, useMemo, useState } from "react";
import styles from "./ProfileList.module.css";
import i18nPackage from "../../i18n/i18n";
import { ViewDetailsModal } from "../edit-quiz-modals";
import { EditQuizViewMoreButton } from "../components/EditQuizButton";
import DeleteProfile from "../edit-quiz-modals/deleteProfile";
import CustomConfigIcon from "../images/profiles/custom-config.svg?react";
import {
	CoreChip,
	CoreFilterButton,
	CoreInfoIndicator,
	CoreMenuButton,
	CoreMenuItem,
	CoreRadioCard,
	CoreRadioCardIcon,
	CoreRadioCardSelectIcon,
	CoreSearchField,
	OverflowText,
} from "../../core";
import { ListItemText, Typography } from "@mui/material";
import {
	getPresetKey,
	getProfileName,
	getUIConfigForProfile,
	ProfileFilterType,
	useFilteredProfiles,
	usePaginatedProfiles,
	useProfileModals,
	useSortedProfiles,
} from "./profileHelpers";
import { PROCTORING_OPTION_TEXT } from "../proctoring-options";
import { ConditionalWrapper, TooltipWrapper } from "../../wrapper";
import { useMobileLayout } from "../../hooks";
import { EditQuizNoResults } from "../proctoring-options/option-lists/OptionListsComponents";
import { PROFILE_CONFIG } from "../edit-quiz-steps/StepIconConfig";

type PresetProfileGridProps = {
	onSelect: (id: number | undefined) => void;
	selectedId: number | undefined;
	presetProfiles: ProctoringOptionProfileV2[];
	liteMode: boolean;
};

/**IF ENFORCED PROFILES, SHOULD NOT BE HERE */
export function PresetProfileGrid({ onSelect, presetProfiles, selectedId, liteMode }: PresetProfileGridProps) {
	// Should be only 3 preset profiles
	const displayProfiles = presetProfiles.slice(0, 3);
	const isMobileLayout = useMobileLayout();
	return (
		<div className={styles.presetProfileGrid}>
			{displayProfiles.map((p) => {
				const isSelected = p.id === selectedId;
				return (
					<ProfileButton
						key={p.id}
						onSelect={() => onSelect(p.id)}
						profile={p}
						selected={isSelected}
						action={<CoreRadioCardSelectIcon selected={isSelected} />}
						liteMode={liteMode}
						isMobileLayout={isMobileLayout}
					/>
				);
			})}
			<CustomConfigButton
				onSelect={() => onSelect(undefined)}
				selected={selectedId === undefined}
				action={<CoreRadioCardSelectIcon selected={selectedId === undefined} />}
				enforcedProfiles={false}
				isMobileLayout={isMobileLayout}
			/>
		</div>
	);
}

function RecommendBadge() {
	return (
		<CoreChip chipColor="#8149E3" className={styles.recBadge}>
			{i18nPackage.t("editQuizPage.profileStep.recommended")}
		</CoreChip>
	);
}

function PresetTooltip({ profile, liteMode }: { profile: ProctoringOptionProfileV2; liteMode?: boolean }) {
	const profileType = getPresetKey(profile);
	if (!profileType) return null;

	const isExtension = profile.proctoringOption.isExtension;

	// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
	const restrictionString = i18nPackage.t(`editQuizPage.profileStep.${profileType}.restrictions`) as string;
	const restrictionList = restrictionString.split("|");
	return (
		<div className={styles.presetTooltip}>
			<Typography className={styles.header}>{i18nPackage.t("editQuizPage.profileStep.configuration")}</Typography>
			<div className={styles.presetGrid}>
				<Typography className={styles.title}>{i18nPackage.t("editQuizPage.profileStep.platform")}</Typography>
				<Typography className={styles.description}>
					{isExtension ? PROCTORING_OPTION_TEXT["isExtension"].header : PROCTORING_OPTION_TEXT["isLockdown"].header}
				</Typography>
				{!liteMode && (
					<>
						<Typography className={styles.title}>{i18nPackage.t("editQuizPage.profileStep.recording")}</Typography>
						<Typography className={styles.description}>
							{i18nPackage.t(`editQuizPage.profileStep.${profileType}.recording`)}
						</Typography>
					</>
				)}
				<Typography className={styles.title}>{i18nPackage.t("editQuizPage.profileStep.restrictions")}</Typography>
				<Typography className={styles.description}>
					{restrictionList.length > 1 ? (
						<ul className={styles.bulletList}>
							{restrictionList.map((item, i) => (
								<li key={i}>{item}</li>
							))}
						</ul>
					) : (
						// Render the first item or the raw string
						restrictionList[0] || restrictionString
					)}
				</Typography>
			</div>
		</div>
	);
}

type ActionMenuProps = {
	onView: () => void;
	onDelete?: () => void;
};

function ActionMenu({ onView, onDelete }: ActionMenuProps) {
	const [menuShowing, setMenuShowing] = useState(false);
	return (
		<CoreMenuButton
			menuShowing={menuShowing}
			setMenuShowing={setMenuShowing}
			aria-label={i18nPackage.t("editQuizPage.profileStep.actionsButtonAriaLabel")}
			className={styles.profileAction}
		>
			<CoreMenuItem
				aria-haspopup="dialog"
				onClick={() => {
					onView();
					setMenuShowing(false);
				}}
			>
				<ListItemText primary={i18nPackage.t("editQuizPage.profileStep.view")} />
			</CoreMenuItem>
			{onDelete && (
				<CoreMenuItem
					onClick={() => {
						onDelete();
						setMenuShowing(false);
					}}
					aria-haspopup="dialog"
				>
					<ListItemText primary={i18nPackage.t("editQuizPage.profileStep.delete")} />
				</CoreMenuItem>
			)}
		</CoreMenuButton>
	);
}

type BaseProfileButtonProps = {
	selected: boolean;
	onSelect: () => void;
	badgeText: string;
	headerChildren?: React.ReactNode;
	icon: React.ReactNode;
	title: string;
	titleChildren?: React.ReactNode;
	description?: string;
	action?: React.ReactNode;
	disabled?: boolean;
	disabledTooltip?: React.ReactNode;
	isMobileLayout: boolean;
};

function BaseProfileButton({
	onSelect,
	badgeText,
	headerChildren,
	icon,
	title,
	titleChildren,
	selected,
	description,
	action,
	disabled,
	disabledTooltip,
	isMobileLayout,
}: BaseProfileButtonProps) {
	return (
		<TooltipWrapper condition={!!disabled && disabledTooltip !== undefined} title={disabledTooltip}>
			<CoreRadioCard
				disabled={disabled}
				slotProps={{
					container: {
						className: styles.profileButtonWrapper,
					},
					actionWrapper: {
						className: styles.headerRow,
					},
					button: {
						className: styles.profileButton,
					},
				}}
				onClick={onSelect}
				selected={selected}
				secondaryAction={
					<>
						<div className={styles.badgeRow}>
							<CoreChip
								chipColor="#F3E5F5"
								className={styles.profileBadge}
								sx={{ color: "#7b1fa2" }}
								variant="rectangular"
							>
								{badgeText}
							</CoreChip>
							{headerChildren}
						</div>
						{action}
					</>
				}
			>
				<CoreRadioCardIcon icon={icon} enabled={selected} width={46} height={48} />
				<div className={styles.titleRow}>
					<ConditionalWrapper
						condition={isMobileLayout}
						whenTrue={(children) => <Typography className={styles.profileName}>{children}</Typography>}
						whenFalse={(children) => <OverflowText className={styles.profileName}>{children}</OverflowText>}
					>
						{title}
					</ConditionalWrapper>
					{titleChildren}
				</div>
				{description && <Typography className={styles.profileDesc}>{description}</Typography>}
			</CoreRadioCard>
		</TooltipWrapper>
	);
}

type ProfileButtonProps = {
	profile: ProctoringOptionProfileV2;
	liteMode?: boolean;
	hideRecommendBadge?: boolean;
} & Pick<BaseProfileButtonProps, "onSelect" | "selected" | "action" | "isMobileLayout">;

function ProfileButton({
	onSelect,
	profile,
	selected,
	action,
	hideRecommendBadge = false,
	liteMode,
	isMobileLayout,
}: ProfileButtonProps) {
	const { proctoringOption } = profile;

	const profileConfig = getUIConfigForProfile(profile);
	const presetKey = getPresetKey(profile);

	if (!profileConfig) return null;

	const titleChildren = presetKey ? (
		<>
			<CoreInfoIndicator
				hint={<PresetTooltip profile={profile} liteMode={liteMode} />}
				slotProps={{
					arrow: {
						className: styles.tooltipArrow,
					},
					tooltip: {
						className: styles.tooltipWrapper,
					},
				}}
			/>
			{presetKey === "standard" && !hideRecommendBadge && <RecommendBadge />}
		</>
	) : null;

	return (
		<BaseProfileButton
			isMobileLayout={isMobileLayout}
			badgeText={
				proctoringOption.isExtension
					? PROCTORING_OPTION_TEXT["isExtension"].header
					: PROCTORING_OPTION_TEXT["isLockdown"].header
			}
			headerChildren={
				profile.type === "assessment" || profile.type === "preset" ? null : (
					<CoreChip
						className={styles.profileBadge}
						chipColor={profileConfig.bgColor}
						sx={{ color: profileConfig.svgColor }}
						variant="rectangular"
					>
						{profileConfig.name}
					</CoreChip>
				)
			}
			title={presetKey ? profileConfig.name : getProfileName(profile)}
			description={profileConfig.description}
			icon={<profileConfig.Icon />}
			onSelect={onSelect}
			selected={selected}
			titleChildren={titleChildren}
			action={action}
		/>
	);
}

type CustomConfigButtonProps = Pick<BaseProfileButtonProps, "onSelect" | "selected" | "action" | "isMobileLayout"> & {
	enforcedProfiles: boolean;
};

function CustomConfigButton({ onSelect, selected, action, enforcedProfiles, isMobileLayout }: CustomConfigButtonProps) {
	return (
		<BaseProfileButton
			onSelect={onSelect}
			selected={selected}
			title={i18nPackage.t("editQuizPage.profileStep.custom.title")}
			description={i18nPackage.t("editQuizPage.profileStep.custom.description")}
			badgeText={i18nPackage.t("editQuizPage.profileStep.custom.badge")}
			icon={<CustomConfigIcon />}
			action={action}
			disabled={enforcedProfiles}
			disabledTooltip={i18nPackage.t("editQuizPage.profileStep.institutionRequiredProfiles")}
			isMobileLayout={isMobileLayout}
		/>
	);
}

type ProfileToolbarProps = {
	onClear: () => void;
	onApply: () => void;
	filterCount: number;
	isFilterChecked: (profileType: ProfileFilterType) => boolean;
	onFilterChecked: (profileType: ProfileFilterType, checked: boolean) => void;
	onSearch: (text: string) => void;
	searchText: string;
	controlId: string;
	totalResults: number;
};

function ProfileToolbar({
	controlId,
	filterCount,
	isFilterChecked,
	onApply,
	onClear,
	onFilterChecked,
	onSearch,
	searchText,
	totalResults,
}: ProfileToolbarProps) {
	const PROFILE_FILTER_KEYS = Object.keys(PROFILE_CONFIG) as Array<ProfileFilterType>;
	return (
		<div className={styles.profileToolbar}>
			<CoreFilterButton
				filterCount={filterCount}
				onClear={onClear}
				onApply={onApply}
				filterSections={[
					{
						alignment: "top",
						sectionTitle: i18nPackage.t("editQuizPage.profileStep.profileType"),
						checkboxes: PROFILE_FILTER_KEYS.map((profile) => ({
							checked: isFilterChecked(profile),
							onChange: (checked) => onFilterChecked(profile, checked),
							children:
								profile === "preset" ? i18nPackage.t("editQuizPage.profileStep.preset") : PROFILE_CONFIG[profile].name,
						})),
					},
				]}
			/>
			<CoreSearchField
				autoComplete="off"
				label={i18nPackage.t("editQuizPage.profileStep.placeholder")}
				onSearch={onSearch}
				ariaControls={controlId}
				totalResults={totalResults}
				value={searchText}
				searchFieldWrapperClassname={styles.profileSearchFieldWrapper}
				noForm
			/>
		</div>
	);
}

export type ProfileListProps = {
	profiles: Array<ProctoringOptionProfileV2>;
	showCustomButton: boolean;

	/** undefined means the custom button is selected */
	selectedId: number | undefined;
	onProfileSelected: (id: number | undefined) => void;

	verityInstituteSettings: VerityInstituteSettings;
	optionRelatedInstituteSettings: OptionRelatedInstituteSettings | undefined;
	proctoringData: Pick<ProctoringDataType, "applications" | "extensions">;
	enforcedProfilesOnly: boolean;
	onDelete: (profile: ProctoringOptionProfileV2) => void;
	canDeleteProfile: (profile: ProctoringOptionProfileV2) => boolean;
};

export default function ProfileList({
	profiles,
	showCustomButton,
	selectedId,
	onProfileSelected,
	optionRelatedInstituteSettings,
	proctoringData,
	verityInstituteSettings,
	enforcedProfilesOnly,
	onDelete,
	canDeleteProfile,
}: ProfileListProps) {
	const { filter, filteredProfiles, search } = useFilteredProfiles(profiles, enforcedProfilesOnly);
	const { gridRef, handleScroll, handleViewMore, isAtBottom, limit, isExpanded } = usePaginatedProfiles(
		profiles,
		selectedId,
		showCustomButton,
		filter.areFiltersApplied,
	);

	const { bottomPresets, hasMoreProfiles, visibleProfiles } = useSortedProfiles(
		filteredProfiles,
		selectedId,
		limit,
		filter.isFilterApplied,
	);

	const { activeDeleteProfile, activeViewProfile, closeDeleteModal, closeViewModal, openDeleteModal, openViewModal } =
		useProfileModals(filteredProfiles);

	const controlId = useId();
	const isMobileLayout = useMobileLayout();

	const shouldShowViewMore = useMemo(
		() => hasMoreProfiles && (!isExpanded || isAtBottom),
		[hasMoreProfiles, isAtBottom, isExpanded],
	);

	return (
		<>
			<div className={styles.profileListContainer}>
				<ProfileToolbar
					controlId={controlId}
					filterCount={filter.filterCount}
					isFilterChecked={filter.isFilterChecked}
					onApply={filter.handleApplyFilter}
					onClear={filter.handleClearFilter}
					onFilterChecked={filter.handleFilterCheckbox}
					onSearch={search.setSearchText}
					searchText={search.searchText}
					totalResults={filteredProfiles.length}
				/>
				{visibleProfiles.length === 0 ? (
					<EditQuizNoResults />
				) : (
					<div
						className={`${styles.profileGrid} ${!isExpanded ? styles.initialState : styles.expandedState}`}
						id={controlId}
						ref={gridRef}
						onScroll={isExpanded ? handleScroll : undefined}
					>
						{visibleProfiles.map((p) => {
							return (
								<ProfileButton
									key={p.id}
									onSelect={() => onProfileSelected(p.id)}
									selected={p.id === selectedId}
									profile={p}
									action={
										p.type === "preset" ? undefined : (
											<ActionMenu
												onView={() => openViewModal(p.id)}
												onDelete={canDeleteProfile(p) ? () => openDeleteModal(p.id) : undefined}
											/>
										)
									}
									hideRecommendBadge={true}
									liteMode={verityInstituteSettings.liteMode}
									isMobileLayout={isMobileLayout}
								/>
							);
						})}
						{isExpanded &&
							bottomPresets.map((p) => {
								return (
									<ProfileButton
										key={p.id}
										onSelect={() => onProfileSelected(p.id)}
										selected={p.id === selectedId}
										profile={p}
										hideRecommendBadge={true}
										liteMode={verityInstituteSettings.liteMode}
										isMobileLayout={isMobileLayout}
									/>
								);
							})}
						{showCustomButton && (
							<CustomConfigButton
								enforcedProfiles={enforcedProfilesOnly}
								onSelect={() => onProfileSelected(undefined)}
								selected={selectedId === undefined}
								isMobileLayout={isMobileLayout}
							/>
						)}
					</div>
				)}

				{shouldShowViewMore && (
					<EditQuizViewMoreButton
						text={i18nPackage.t("editQuizPage.profileStep.seeMoreProfiles")}
						onClick={handleViewMore}
					/>
				)}
			</div>
			{activeViewProfile && (
				<ViewDetailsModal
					title={i18nPackage.t("editQuizPage.profileStep.viewProfileTitle", {
						profile: getProfileName(activeViewProfile),
					})}
					description={i18nPackage.t("editQuizPage.profileStep.viewProfileDesc")}
					onClose={closeViewModal}
					profile={activeViewProfile}
					configuration={{
						optionRelatedInstituteSettings,
						proctoringData,
						proctoringOption: activeViewProfile.proctoringOption,
						verityInstituteSettings,
					}}
				/>
			)}
			{activeDeleteProfile && (
				<DeleteProfile
					profile={activeDeleteProfile}
					onClose={closeDeleteModal}
					onDelete={() => onDelete(activeDeleteProfile)}
				/>
			)}
		</>
	);
}
